import type { DropdownOption } from '../DropdownOption/DropdownOption';

import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import { CloseIcon } from '../../../images/shapes';

export type DropdownRemoveOptionFn = (option: DropdownOption) => void;

interface SelectedOptionsBaseProps {
  /**
   * Placeholder for no value selected
   */
  placeholder?: string;
  /**
   * If dropdown is disabled
   */
  disabled?: boolean;
  /**
   * Test id for dropdown
   */
  testId?: string;
  /**
   * Disable red-styled label on unfounded element
   */
  ignoreNotFound?: boolean;
  /**
   * If dropdown is multiple and should show limited selected options
   */
  maxVisibleValues?: number;
  /**
   * If max visible values is provided and the default text should be overridden
   */
  maxVisibleValuesText?: string;
  /**
   * If the selected options are being searched
   */
  isSearch?: boolean;
  /**
   * Selected option for delete
   */
  selectedForDelete?: null | string;
  /**
   * Function to call when an option is removed
   */
  onRemove: DropdownRemoveOptionFn;
}

type SelectedOptionsProps =
  | (SelectedOptionsBaseProps & {
      /**
       * If multiple options can be selected
       */
      multiple: true;
      /**
       * Selected options to display
       */
      selectedOptions: DropdownOption[];
    })
  | (SelectedOptionsBaseProps & {
      /**
       * If multiple options can be selected
       */
      multiple: false | undefined;
      /**
       * Selected options to display
       */
      selectedOptions: DropdownOption;
    });

const SelectedOptions = ({
  placeholder,
  multiple,
  selectedOptions,
  onRemove,
  disabled,
  ignoreNotFound,
  testId,
  isSearch,
  maxVisibleValues,
  maxVisibleValuesText,
  selectedForDelete,
}: SelectedOptionsProps) => {
  const { t } = useTranslation();
  const placeholderColor = disabled
    ? 'text-slate-600'
    : 'text-slate-400 font-light';

  if (!multiple || !selectedOptions?.length) {
    const selectedOption = selectedOptions as DropdownOption;
    return (
      <div
        className={twMerge(
          !selectedOption?.label && placeholderColor,
          !ignoreNotFound && selectedOption?.notFound && 'text-red',
        )}
      >
        {selectedOption?.label || placeholder}
      </div>
    );
  }

  return selectedOptions.map((selectedOption, idx) => {
    if (maxVisibleValues && idx >= maxVisibleValues)
      return idx === selectedOptions.length - 1
        ? maxVisibleValuesText ||
            t('Global.DropdownMore', {
              value: selectedOptions.length - maxVisibleValues,
            })
        : null;

    if (selectedOption.ignoreCheckmark) return '';

    return (
      <div
        key={selectedOption.value}
        className={twMerge(
          'inline-flex items-center px-2',
          'bg-blue-200 rounded-sm cursor-default dark:bg-blue-300',
          !ignoreNotFound && selectedOption.notFound && 'bg-red text-white',
          'max-w-full shrink-0 min-w-0',
          maxVisibleValues === 1 && 'max-w-[70%]',
          selectedForDelete === selectedOption.value && 'border border-red',
        )}
      >
        <span
          className={twMerge(
            'overflow-hidden py-px dark:text-slate-950 truncate',
            isSearch && 'min-h-[14px] min-w-[14px]',
          )}
          title={
            typeof selectedOption.label === 'string'
              ? selectedOption.label
              : undefined
          }
        >
          {selectedOption.label || selectedOption.value}
        </span>

        {!disabled && (
          <button
            className={
              'flex items-center bg-transparent! p-1! border-none! ml-1 hover:bg-blue! rounded-full group'
            }
            onClick={(e) => {
              e.stopPropagation();
              onRemove(selectedOption);
            }}
            type="button"
            {...getTestProps(testId, `${selectedOption.value}-remove`)}
          >
            <CloseIcon
              className="w-2 h-2 text-black group-hover:text-white
            transition duration-200 ease-in-out dark:text-slate-950 dark:group-hover:text-white"
            />
          </button>
        )}
      </div>
    );
  });
};

export default SelectedOptions;
